import { useEffect, useRef } from "react";
import Reveal from "@/components/Reveal";

/**
 * Verdict Stamps — the three Bearing outcomes thunked in, staggered.
 * GO. · CONDITIONAL GO. · WAIT.
 */
const stamps = [
  {
    txt: "GO.",
    tone: "go",
    tilt: "",
    sub: (
      <>
        The opportunity is clear, the brand is ready, the economics work. Comes with the
        market sequence and a <strong className="text-white font-medium">week-by-week plan.</strong>
      </>
    ),
  },
  {
    txt: "CONDITIONAL\u00A0GO.",
    tone: "cond",
    tilt: "tilt-pos",
    sub: (
      <>
        Strong opportunity — with <strong className="text-white font-medium">named conditions</strong> to clear
        first. Each one specific, ordered, time-estimated. Most brands land here.
      </>
    ),
    small: true,
  },
  {
    txt: "WAIT.",
    tone: "wait",
    tilt: "tilt-flat",
    sub: (
      <>
        Launching now would waste your money. We say so, explain why, and tell you{" "}
        <strong className="text-white font-medium">exactly what changes the verdict.</strong>
      </>
    ),
  },
];

const toneClass = {
  go: "text-verdict-go",
  cond: "text-verdict-cond",
  wait: "text-verdict-wait",
};

export default function VerdictStamps() {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!rootRef.current) return;
    const root = rootRef.current;
    const els = root.querySelectorAll("[data-stamp]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            els.forEach((el, i) => setTimeout(() => el.classList.add("stamp-in"), i * 260));
            io.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(root);
    return () => io.disconnect();
  }, []);

  return (
    <section
      data-testid="verdict-stamps-section"
      className="bg-navy-deep text-white overflow-hidden border-b"
      style={{ borderColor: "rgba(255,255,255,0.08)" }}
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-coral flex items-center gap-3.5 mb-5">
            <span className="w-9 h-px bg-coral" />
            Chapter 02 — The Bearing
          </p>
          <h2 className="font-serif-i italic text-[34px] sm:text-[44px] lg:text-[64px] leading-[.98] tracking-[-.035em] max-w-3xl mb-6">
            Every assessment ends with
            <br />a <em className="text-coral">stamp.</em>
          </h2>
          <p className="text-[16px] font-light leading-[1.78] max-w-[600px] mb-14" style={{ color: "rgba(255,255,255,.48)" }}>
            Data tools hand you forty metrics and leave the decision to you. The Bearing reads
            your brand — website, pricing, category, readiness — and hands you the decision.{" "}
            <strong className="text-white font-medium">Free to request. Yours to keep, whoever you launch with.</strong>
          </p>
        </Reveal>

        <div ref={rootRef} className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {stamps.map((s, i) => (
            <div key={i} className="text-center pt-8 px-2.5" data-testid={`stamp-cell-${s.tone}`}>
              <div
                data-stamp
                className={`stamp ${s.tilt} ${toneClass[s.tone]}`}
                style={{
                  fontSize: s.small
                    ? "clamp(22px,2.1vw,30px)"
                    : "clamp(36px,3.6vw,52px)",
                  textShadow: s.tone === "go" ? "0 0 34px rgba(63,175,110,.25)" : undefined,
                }}
              >
                {s.txt}
              </div>
              <p
                className="text-[13px] font-light leading-[1.7] max-w-[300px] mx-auto mt-8"
                style={{ color: "rgba(255,255,255,.5)" }}
              >
                {s.sub}
              </p>
            </div>
          ))}
        </div>

        {/* Verdict truth quote */}
        <Reveal delay={0.1}>
          <div
            className="mt-20 pt-10 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-6 sm:gap-10 items-center"
            style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}
            data-testid="verdict-truth"
          >
            <div className="font-serif-i italic text-coral leading-[.8] tracking-[-.05em]" style={{ fontSize: "90px" }}>
              &ldquo;
            </div>
            <div
              className="font-serif-i italic tracking-[-.02em] leading-[1.32] max-w-[760px]"
              style={{
                fontSize: "clamp(20px,2.4vw,30px)",
                color: "rgba(255,255,255,.85)",
              }}
            >
              We stamp <b className="text-coral font-normal">WAIT</b> more often than you'd expect. An
              agency that never turns work away isn't assessing your brand — it's processing
              your card. The verdict you can trust is the one that's{" "}
              <b className="text-coral font-normal">allowed to be no.</b>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
